/**
 * ST-Operations-os#86 / #74 — GET idempotente sai UMA vez por dono e chave.
 *
 * O defeito: cada montagem e cada chamador disparava o seu GET da mesma coisa
 * (memory-settings, artifacts, histórico, lista de conversas), e abrir/voltar/
 * remontar a mesma conversa multiplicava as chamadas até o motor responder 429
 * para tudo — inclusive `/auth/status`.
 *
 * Duas travas, só para leitura:
 *   - VOO ÚNICO: quem pede o que já está voando recebe a MESMA promessa;
 *   - MEMÓRIA CURTA (`ttlMs`): resposta que acabou de chegar é reaproveitada.
 * A chave carrega inquilino + usuário: dono diferente nunca lê a resposta de
 * outro. Erro nunca é guardado — a próxima chamada vai à rede.
 */
import { useAuthStore } from '@/stores/authStore'

type Slot = { promise: Promise<unknown>; expiresAt: number }
const slots = new Map<string, Slot>()

function ownerKey(key: string): string {
  const { tenantId, userId } = useAuthStore.getState()
  return `${tenantId ?? ''}\u0000${userId ?? ''}\u0000${key}`
}

export function dedupedGet<T>(key: string, load: () => Promise<T>, ttlMs: number): Promise<T> {
  const id = ownerKey(key)
  const now = Date.now()
  const slot = slots.get(id)
  if (slot && slot.expiresAt > now) return slot.promise as Promise<T>
  const promise = load()
  const entry: Slot = { promise, expiresAt: Number.POSITIVE_INFINITY }
  slots.set(id, entry)
  promise.then(
    () => {
      if (slots.get(id) !== entry) return
      if (ttlMs > 0) entry.expiresAt = Date.now() + ttlMs
      else slots.delete(id)
    },
    () => {
      if (slots.get(id) === entry) slots.delete(id)
    },
  )
  return promise
}

/** Depois de uma escrita, a próxima leitura daquela chave vai à rede. */
export function invalidateDedupedGet(key: string): void {
  slots.delete(ownerKey(key))
}

/** A lista de conversas mudou aqui (criou, renomeou, apagou): a próxima leitura vai à rede. */
export function invalidateConversationList(): void {
  invalidateDedupedGet(CONVERSATION_LIST_PATH)
}

export const CONVERSATION_LIST_PATH = '/api/v1/conversations'

/** Só para testes. */
export function resetRequestDedupeForTests(): void {
  slots.clear()
}
